import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Observable, from, of, Subscription, firstValueFrom, Subject, EMPTY } from 'rxjs';
import {
	catchError,
	distinctUntilChanged,
	filter,
	finalize,
	map,
	mergeMap,
	switchMap,
	take,
	takeUntil,
	tap,
	toArray
} from 'rxjs/operators';
import { GlobalService } from '../global';
import { BusinessQuery } from '../business';
import { SessionQuery } from '../session';
import {
	DuplicateFileDialogComponent,
	DuplicateFileDialogData,
	DuplicateFileDialogResult,
	DuplicateResolution
} from '../../../shared/components/duplicate-file-dialog/duplicate-file-dialog.component';
import { createFolderPermissionDeniedMessage } from './file-library-permission-messages';
import { UploadQueueService } from './services/upload-queue.service';
import { v4 as uuidv4 } from 'uuid';
import { environment } from '../../../../environments/environment';
import {
	ActiveFilters,
	BatchOperationResult,
	BulkOperationResult,
	BulkUpdateTagsRequest,
	CompletedUpload,
	CompleteUploadRequest,
	createEmptyFilters,
	CreateUploadSessionRequest,
	CreateUploadSessionResponse,
	FileCollection,
	FileCollectionType,
	FileNodeAttributes,
	FileSystemNode,
	FilterOptions,
	FolderTreeNode,
	InitiateUploadRequest,
	InitiateUploadResponse,
	MultipartUploadResponse,
	PaginatedDirectoryContent,
	PostUploadResponse,
	SearchFileSystemRequest,
	SearchResult,
	starredCollectionId,
	SuggestFileSystemRequest,
	UpdateMetadataRequest,
	UpdateTagsRequest,
	UploadingFile,
	UploadPartResult,
	UploadSignal
} from './file-library.model';
import { FileLibrarySearchFilter } from '../file-library-search-filters';
import { FileLibraryStore } from './file-library.store';

// ============================================================================
// Metadata Extraction Helpers
// ============================================================================

/**
 * Parses a duration string (HH:MM:SS.ff or MM:SS.ff) to seconds.
 * Examples: "00:02:46.87" -> 166.87, "1:30.5" -> 90.5
 */
function parseDurationToSeconds(duration: string | undefined | null): number | undefined {
	if (!duration || typeof duration !== 'string') return undefined;

	const parts = duration.split(':');
	if (parts.length < 2 || parts.length > 3) return undefined;

	let hours = 0;
	let minutes = 0;
	let seconds = 0;

	if (parts.length === 3) {
		hours = parseFloat(parts[0]) || 0;
		minutes = parseFloat(parts[1]) || 0;
		seconds = parseFloat(parts[2]) || 0;
	} else {
		minutes = parseFloat(parts[0]) || 0;
		seconds = parseFloat(parts[1]) || 0;
	}

	const totalSeconds = hours * 3600 + minutes * 60 + seconds;
	return totalSeconds > 0 ? totalSeconds : undefined;
}

/**
 * Parses a resolution string (WxH) to width and height.
 * Examples: "1440x1080" -> { width: 1440, height: 1080 }
 */
function parseResolution(resolution: string | undefined | null): { width: number; height: number } | undefined {
	if (!resolution || typeof resolution !== 'string') return undefined;

	const match = resolution.match(/^(\d+)x(\d+)$/);
	if (!match) return undefined;

	const width = parseInt(match[1], 10);
	const height = parseInt(match[2], 10);

	if (width > 0 && height > 0) {
		return { width, height };
	}
	return undefined;
}

/**
 * Extracts width, height, duration, and frameRate from nested API metadata.
 * Handles both image and video metadata structures.
 */
function extractMediaMetadata(metadata: Record<string, unknown> | undefined | null): {
	width?: number;
	height?: number;
	duration?: number;
	frameRate?: number;
} {
	if (!metadata) return {};

	const result: { width?: number; height?: number; duration?: number; frameRate?: number } = {};

	// Check for image metadata (width/height at top level)
	if (typeof metadata['width'] === 'number' && metadata['width'] > 0) {
		result.width = metadata['width'] as number;
	}
	if (typeof metadata['height'] === 'number' && metadata['height'] > 0) {
		result.height = metadata['height'] as number;
	}

	// Check for video metadata (duration at top level, resolution in streams)
	if (typeof metadata['duration'] === 'string') {
		result.duration = parseDurationToSeconds(metadata['duration'] as string);
	}

	// Extract from streams array (video files)
	const streams = metadata['streams'] as Array<Record<string, unknown>> | undefined;
	if (Array.isArray(streams)) {
		for (const stream of streams) {
			if (stream['type'] === 'Video') {
				// Parse resolution if we don't already have width/height
				if (!result.width && !result.height && typeof stream['resolution'] === 'string') {
					const dims = parseResolution(stream['resolution'] as string);
					if (dims) {
						result.width = dims.width;
						result.height = dims.height;
					}
				}
				// Parse frame rate
				if (typeof stream['frameRate'] === 'string') {
					const fr = parseFloat(stream['frameRate'] as string);
					if (fr > 0) {
						result.frameRate = fr;
					}
				}
				break; // Use first video stream
			}
		}
	}

	return result;
}

/**
 * Upload result structure containing tempId and observable
 */
export interface UploadResultObservable {
	tempId: string;
	upload$: Observable<UploadSignal>;
}

/**
 * File Library Service
 * Handles all file library API calls and store updates.
 */
@Injectable({ providedIn: 'root' })
export class FileLibraryService implements OnDestroy {
	private readonly endpoint = `${environment.apiUrl}/site/${environment.organizationId}/file-library`;
	private uploadSubscriptions = new Map<string, Subscription>();
	private uploadMetadata = new Map<
		string,
		{ uploadId?: string; uploadCompleted: boolean; sessionId?: string | null; aborted?: boolean }
	>();
	private starredItemIds = new Set<string>();
	private starredOverrides = new Map<string, boolean>();

	/**
	 * Subject that emits when business context changes.
	 * Used to cancel in-flight requests and reset state.
	 */
	private readonly businessSwitch$ = new Subject<void>();

	/**
	 * Subject for cleanup on service destroy.
	 */
	private readonly destroy$ = new Subject<void>();

	constructor(
		private readonly fileLibraryStore: FileLibraryStore,
		private readonly http: HttpClient,
		private readonly dialog: MatDialog,
		private readonly uploadQueueService: UploadQueueService,
		private readonly businessQuery: BusinessQuery,
		private readonly sessionQuery: SessionQuery,
		private readonly router: Router,
		private readonly globalService: GlobalService
	) {
		this.subscribeToBusinessChanges();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
		this.businessSwitch$.complete();
	}

	/**
	 * Debug logging helper - only logs in development mode
	 */
	private debugLog(message: string, ...args: unknown[]): void {
		if (!environment.production) {
			console.log(message, ...args);
		}
	}

	/**
	 * Sets the directory tree in the store
	 */
	setDirectoryTree(directoryTree: FolderTreeNode[]): void {
		this.fileLibraryStore.update({ directoryTree });
	}

	/**
	 * Sets the loading state in the store
	 */
	setLoading(loading: boolean): void {
		this.fileLibraryStore.update({ loading });
	}

	/**
	 * Sets the error state in the store
	 */
	setError(error: string | null): void {
		this.fileLibraryStore.update({ error });
	}

	/**
	 * Sets the directory content in the store
	 * @param directoryContent - The directory content data
	 * @param append - Whether to append to existing content (for load more) or replace it
	 */
	setDirectoryContent(directoryContent: FileSystemNode[], append: boolean = false): void {
		const enrichedContent = this.enrichWithStarredMetadata(directoryContent);

		if (append) {
			const state = this.fileLibraryStore.getValue();
			const updatedContent = [...state.directoryContent, ...enrichedContent];
			this.fileLibraryStore.update({ directoryContent: updatedContent });
		} else {
			this.fileLibraryStore.update({ directoryContent: enrichedContent });
		}
	}

	/**
	 * Clears the directory content in the store.
	 * Called when a fresh (non-append) load starts so nothing keeps rendering the previous
	 * folder/collection while the request is in flight. The grid already shows its skeleton
	 * while `loadingContent` is true and the empty state is gated on `!isLoading`, so this
	 * only removes stale content from derived UI (item count, keyboard navigation, select all).
	 */
	clearDirectoryContent(): void {
		this.fileLibraryStore.update({ directoryContent: [] });
	}

	/**
	 * Forgets the folder role held in the store.
	 *
	 * Only `getDirectoryContent` carries a `userRole`, because only it is scoped to one
	 * folder. Search results, starred items and collections span many folders with
	 * different roles, so whatever role the last-visited folder left behind is
	 * meaningless there - and if that role was 'viewer' it would wrongly hide actions on
	 * items the user actually administers. Clearing it back to null restores "unknown",
	 * which reads as allow, leaving the API to reject anything the user may not do.
	 */
	clearCurrentFolderUserRole(): void {
		this.fileLibraryStore.update({ currentFolderUserRole: null });
	}

	/**
	 * Removes items from the directory content store.
	 *
	 * Destructive operations (delete, move-away) must go through here rather than filtering a
	 * component-local copy of the list: `directoryContent$` re-derives that list on every
	 * `uploadingFiles` change, so content left behind in the store reappears on screen as soon
	 * as an upload starts or ticks progress (#10721).
	 *
	 * Writes once for the whole batch, so a multi-item delete produces a single emission.
	 * `totalItems` is decremented by the number actually present, keeping pagination bookkeeping
	 * in step; `hasMore` is left alone since the server-side remainder has not changed.
	 *
	 * @param itemIds - IDs to remove; ids not present in the store are ignored
	 */
	removeItemsFromDirectoryContent(itemIds: string[]): void {
		if (itemIds.length === 0) {
			return;
		}

		const idsToRemove = new Set(itemIds);
		const state = this.fileLibraryStore.getValue();
		const directoryContent = state.directoryContent.filter(item => !idsToRemove.has(item.id));
		const removedCount = state.directoryContent.length - directoryContent.length;

		if (removedCount === 0) {
			return;
		}

		this.fileLibraryStore.update({
			directoryContent,
			totalItems: Math.max(0, state.totalItems - removedCount)
		});
	}

	/**
	 * Updates a single item in the directory content store
	 */
	updateItemInDirectoryContent(updatedNode: FileSystemNode, itemId: string): void {
		const state = this.fileLibraryStore.getValue();
		const currentContent = state.directoryContent;

		const updatedContent = currentContent.map(item => (item.id === itemId ? updatedNode : item));

		if (currentContent.some(item => item.id === itemId)) {
			const enrichedContent = this.enrichWithStarredMetadata(updatedContent);
			this.fileLibraryStore.update({ directoryContent: enrichedContent });
		}
	}

	/**
	 * Sets pagination state in the store
	 */
	setPaginationState(pagination: { currentPage: number; totalItems: number; hasMore: boolean }): void {
		this.fileLibraryStore.update(pagination);
	}

	/**
	 * Resets pagination state to initial values
	 */
	resetPaginationState(): void {
		this.fileLibraryStore.update({
			currentPage: 1,
			totalItems: 0,
			hasMore: false
		});
	}

	/**
	 * Adds a placeholder file node for an uploading file
	 */
	addUploadingFile(file: File, directoryId?: string): string {
		const tempId = `uploading-${uuidv4()}`;
		const uploadingFileNode: FileSystemNode = {
			id: tempId,
			slug: file.name,
			name: file.name,
			description: '',
			extension: file.name.split('.').pop() || null,
			mimeType: file.type,
			userName: 'Uploading...',
			path: '',
			type: 'file',
			size: file.size,
			createdAt: new Date().toISOString(),
			version: 0,
			tags: [],
			isUploading: true,
			uploadProgress: 0,
			directoryId: directoryId ?? null
		};

		const uploadingFile: UploadingFile = {
			tempId,
			file: uploadingFileNode,
			directoryId: directoryId ?? null
		};

		const state = this.fileLibraryStore.getValue();
		const uploadingFiles = [...state.uploadingFiles, uploadingFile];
		this.fileLibraryStore.update({ uploadingFiles });

		return tempId;
	}

	/**
	 * Updates upload progress for a file
	 */
	updateUploadProgress(tempId: string, progress: number): void {
		const state = this.fileLibraryStore.getValue();
		const uploadingFiles = state.uploadingFiles.map<UploadingFile>(uf =>
			uf.tempId === tempId ? { ...uf, file: { ...uf.file, uploadProgress: progress } } : uf
		);
		this.fileLibraryStore.update({ uploadingFiles });
	}

	/**
	 * Removes the uploading placeholder file
	 */
	removeUploadingFile(tempId: string, success: boolean = true): void {
		const state = this.fileLibraryStore.getValue();

		// Find the uploading file to record completion
		const uploadingFile = state.uploadingFiles.find(uf => uf.tempId === tempId);
		if (uploadingFile) {
			const completedUpload: CompletedUpload = {
				tempId,
				fileName: uploadingFile.file.name,
				directoryId: uploadingFile.directoryId,
				success,
				completedAt: Date.now()
			};
			this.fileLibraryStore.update({
				completedUploads: [...state.completedUploads, completedUpload]
			});
		}

		const uploadingFiles = state.uploadingFiles.filter(uf => uf.tempId !== tempId);
		this.fileLibraryStore.update({ uploadingFiles });

		this.uploadMetadata.delete(tempId);

		// Drop the registry entry but do NOT unsubscribe. This runs from inside
		// startUpload's own tap(), while the stream is delivering its next/error -
		// unsubscribing there closed the subscriber mid-delivery, so the error never
		// reached the caller's onError and a failed upload was reported nowhere.
		// Cancellation still tears the subscription down: cancelUpload() unsubscribes
		// explicitly before calling this, and ngOnDestroy() sweeps whatever is left.
		this.uploadSubscriptions.delete(tempId);
	}

	/**
	 * Cancels an ongoing file upload
	 */
	cancelUpload(tempId: string): void {
		const metadata = this.uploadMetadata.get(tempId);

		if (metadata?.uploadId && !metadata.uploadCompleted && !metadata.aborted) {
			metadata.aborted = true;
			this.uploadMetadata.set(tempId, metadata);
			this.abortUpload(metadata.uploadId)
				.pipe(take(1))
				.subscribe({
					error: error => {
						console.warn('Failed to abort upload', { tempId, uploadId: metadata.uploadId, error });
					}
				});
		}

		const subscription = this.uploadSubscriptions.get(tempId);
		if (subscription) {
			subscription.unsubscribe();
			this.uploadSubscriptions.delete(tempId);
		}

		this.removeUploadingFile(tempId, false);
	}

	/**
	 * Clears the completed uploads list.
	 * Call this after the user dismisses the completion notification.
	 */
	clearCompletedUploads(): void {
		this.fileLibraryStore.update({ completedUploads: [] });
	}

	/**
	 * Sets the selected directory ID in the store
	 */
	setSelectedDirectoryId(selectedDirectoryId: string | null): void {
		this.fileLibraryStore.update({ selectedDirectoryId });
	}

	/**
	 * True when `directoryId` is the folder currently on screen.
	 *
	 * Used to keep background refreshes (upload completions) from writing another
	 * folder's content into the shared `directoryContent` slot after the user has
	 * navigated away. Normalizes the root folder's null / '__root' duality.
	 */
	isViewingDirectory(directoryId: string): boolean {
		const selected = this.fileLibraryStore.getValue().selectedDirectoryId ?? '__root';

		return selected === (directoryId || '__root');
	}

	/**
	 * Sets the content loading state in the store
	 */
	setLoadingContent(loadingContent: boolean): void {
		this.fileLibraryStore.update({ loadingContent });
	}

	/**
	 * Sets the pagination loading state (loading more items) in the store.
	 * Used for infinite scroll/pagination - keeps existing content visible while loading more.
	 */
	setLoadingMore(loadingMore: boolean): void {
		this.fileLibraryStore.update({ loadingMore });
	}

	/**
	 * Marks content as cached for the given cache key.
	 * Called after successful content load to prevent duplicate API calls.
	 */
	setContentCache(cacheKey: string): void {
		this.fileLibraryStore.update({
			contentCacheKey: cacheKey,
			contentCacheTimestamp: Date.now()
		});
	}

	/**
	 * Invalidates the content cache, forcing a reload on next navigation.
	 * Called after mutations (delete, move, rename).
	 */
	invalidateContentCache(): void {
		this.fileLibraryStore.update({
			contentCacheKey: null,
			contentCacheTimestamp: 0
		});
	}

	/**
	 * The business whose starting-folder boundary the API should apply to this request.
	 *
	 * ONLY the actively selected business. Every boundary-scoped endpoint (tree, directory
	 * content, search, suggest) must send the same value, or the API resolves a different
	 * boundary per endpoint and search stops agreeing with the tree the user is looking at.
	 *
	 * Returns null when no business is active, which is deliberate rather than a gap: the
	 * API's `getEffectiveBoundary` grants FULL access when ANY of the user's businesses
	 * lacks a starting folder, since an unbounded business confers the higher privilege.
	 * Nominating a business here used to override that — picking the one business that had
	 * a folder confined a multi-business user to its subtree, so a super-admin in five
	 * businesses could see seven folder trees but search only one of them. Server-side
	 * resolution already handles every case correctly, including the single-business user
	 * whose boundary #10774 depends on, so let it.
	 *
	 * Reading only the active business also matches `StartingFolderService`, keeping the
	 * frontend's idea of the current scope and the API's in agreement.
	 */
	private resolveBoundaryBusinessId(): string | null {
		const activeBusinessId = this.businessQuery.getActiveId();
		return activeBusinessId ? String(activeBusinessId) : null;
	}

	/** Adds the boundary business context to a param bag when one is resolvable. */
	private withBoundaryBusinessId(params: Record<string, string>): Record<string, string> {
		const businessId = this.resolveBoundaryBusinessId();
		if (businessId) {
			params['businessId'] = businessId;
		}
		return params;
	}

	/**
	 * Fetches the directory tree
	 * @param fullTree - When true, super-admins receive the full tree without boundary filtering.
	 *                   Used for admin folder pickers. Ignored for non-super-admin users.
	 */
	getDirectoryTree(fullTree: boolean = false): Observable<FolderTreeNode[]> {
		const url = `${this.endpoint}/directory/tree`;
		const params: Record<string, string> = {};

		if (fullTree) {
			params['fullTree'] = 'true';
		}

		this.withBoundaryBusinessId(params);

		this.setLoading(true);
		this.setError(null);
		this.fileLibraryStore.update({ treeError: null });

		return this.http.get<FolderTreeNode[]>(url, { params }).pipe(
			tap({
				next: directoryTree => {
					this.setDirectoryTree(directoryTree);
					this.setLoading(false);
				},
				error: error => {
					const message = error.message || 'Failed to load directory tree';
					this.setError(message);
					this.fileLibraryStore.update({ treeError: message });
					this.setLoading(false);
				}
			})
		);
	}

	/**
	 * Fetches the content of a specific directory
	 *
	 * @param options.discardIfNavigatedAway - For background refreshes only. Drops the
	 *   response if the user is no longer viewing `directoryId` by the time it lands,
	 *   instead of overwriting the list they are actually looking at. Opt-in because
	 *   the asset/image picker dialogs browse folders other than the selected one and
	 *   legitimately need their content written.
	 * @param options.redirectOnForbidden - Defaults to true: a 403 shows a toast, navigates to
	 *   the File Library root and ends the stream silently. Pickers embedded in other pages
	 *   (editor, page layout) pass false so a 403 is rethrown to their own error handler
	 *   instead of pulling the user off the page.
	 */
	getDirectoryContent(
		directoryId: string,
		updateSelectedDirectory: boolean = true,
		page: number = 1,
		append: boolean = false,
		type?: string,
		starred?: boolean,
		options?: { discardIfNavigatedAway?: boolean; redirectOnForbidden?: boolean }
	): Observable<FileSystemNode[]> {
		const state = this.fileLibraryStore.getValue();
		const perPage = state.perPage;
		const url = `${this.endpoint}/directory/${directoryId}/content`;

		// Use loadingMore for pagination (append=true) to keep existing content visible,
		// loadingContent for initial load (append=false) to show full skeleton
		if (append) {
			this.setLoadingMore(true);
		} else {
			this.setLoadingContent(true);
		}
		if (updateSelectedDirectory) {
			this.setSelectedDirectoryId(directoryId === '__root' ? null : directoryId);
		}

		if (!append) {
			this.resetPaginationState();
			this.clearDirectoryContent();
			// Clear starred collections when loading a fresh directory to prevent memory leak
			// These grow unbounded as users browse - clearing on directory change keeps memory bounded
			this.starredItemIds.clear();
			this.starredOverrides.clear();
		}

		this.setError(null);

		const params: Record<string, string> = {
			page: page.toString(),
			perPage: perPage.toString()
		};

		this.withBoundaryBusinessId(params);

		if (type && type !== 'all') {
			params['type'] = type;
		}

		if (starred !== undefined) {
			params['starred'] = starred.toString();
		}

		return this.http
			.get<{
				items: FileSystemNode[];
				page: number;
				perPage: number;
				totalResults?: number;
				numPages?: number;
				hasMore: boolean;
				userRole?: 'viewer' | 'admin';
			}>(url, { params })
			.pipe(
				// Map items to extract media metadata from nested metadata object
				map(response => {
					const mappedItems = response.items.map(item => ({
						...item,
						// Extract width/height/duration/frameRate from nested metadata
						...extractMediaMetadata(item.metadata as Record<string, unknown> | undefined)
					}));
					return { ...response, items: mappedItems };
				}),
				tap({
					next: response => {
						// The user navigated while this background refresh was in flight.
						// Drop the whole response - content and pagination - so this folder's
						// files don't land in the list that is now on screen.
						if (options?.discardIfNavigatedAway && !this.isViewingDirectory(directoryId)) {
							if (append) {
								this.setLoadingMore(false);
							} else {
								this.setLoadingContent(false);
							}

							return;
						}

						const itemsReturned = response.items?.length || 0;
						const numPages = (response as Record<string, unknown>)['numPages'] as number | undefined;
						const totalResults = (response as Record<string, unknown>)['totalResults'] as number | undefined;

						this.setDirectoryContent(response.items, append);

						// Hydrate the user's role on this folder for permissions UI / toolbar gating.
						// `userRole` is only present when the API's FOLDER_GRANTS_PROXY flag is on.
						if (response.userRole !== undefined) {
							this.fileLibraryStore.update({ currentFolderUserRole: response.userRole });
						}

						const loadedItemsCount = append ? state.totalItems + itemsReturned : itemsReturned;

						const currentPage = response.page || page;
						const calculatedHasMore =
							numPages !== undefined
								? currentPage < numPages
								: totalResults !== undefined
								? loadedItemsCount < totalResults
								: itemsReturned >= perPage;

						this.setPaginationState({
							currentPage,
							totalItems: loadedItemsCount,
							hasMore: calculatedHasMore
						});

						// Reset the appropriate loading flag based on operation type
						if (append) {
							this.setLoadingMore(false);
						} else {
							this.setLoadingContent(false);
						}
					},
					error: error => {
						console.error('Failed to load directory content:', error);
						this.setError(error.message || 'Failed to load directory content');
						// Reset both loading flags on error to be safe
						this.setLoadingContent(false);
						this.setLoadingMore(false);
					}
				}),
				catchError((error: HttpErrorResponse) => {
					// Handle 403 Forbidden - user doesn't have access to this folder
					if (error.status === 403 && options?.redirectOnForbidden !== false) {
						this.globalService.triggerCustomMessage(
							$localize`:403 boundary error message@@fileLibrary403BoundaryError:You don't have access to this folder. Contact your administrator for access.`,
							'danger'
						);
						// Navigate to file library root
						this.router.navigate(['/file-library']);
						// Reset loading states
						this.setLoadingContent(false);
						this.setLoadingMore(false);
						return EMPTY;
					}
					// Re-throw other errors for the tap error handler
					throw error;
				}),
				map(response => response.items)
			);
	}

	canReadDirectory(directoryId: string): Observable<boolean> {
		const url = `${this.endpoint}/directory/${directoryId}/content`;
		const params: Record<string, string> = {
			page: '1',
			perPage: '1'
		};
		const businessIdForBoundary = this.getBusinessIdForBoundary();
		if (businessIdForBoundary) {
			params['businessId'] = businessIdForBoundary;
		}

		return this.http.get(url, { params }).pipe(
			map(() => true),
			catchError(() => of(false))
		);
	}

	private getBusinessIdForBoundary(): string | null {
		const activeBusinessId = this.businessQuery.getActiveId();
		if (activeBusinessId) {
			return String(activeBusinessId);
		}

		const userBusinesses = this.sessionQuery.getBusinesses();
		if (userBusinesses?.length === 1) {
			return userBusinesses[0].id;
		}
		if (userBusinesses?.length > 1) {
			const businessWithBoundary = userBusinesses.find(b => b.startingFolderId);
			return businessWithBoundary?.id || userBusinesses[0].id;
		}
		return null;
	}

	/**
	 * Loads the next page of directory content
	 */
	loadMoreDirectoryContent(directoryId: string, type?: string, starred?: boolean): Observable<FileSystemNode[]> {
		const state = this.fileLibraryStore.getValue();
		const nextPage = state.currentPage + 1;

		return this.getDirectoryContent(directoryId, false, nextPage, true, type, starred);
	}

	/**
	 * Searches files and folders
	 */
	searchFiles(
		request: SearchFileSystemRequest,
		append: boolean = false,
		options?: { toastOnForbidden?: boolean }
	): Observable<FileSystemNode[]> {
		const url = `${this.endpoint}/search`;

		// Use loadingMore for pagination, loadingContent for initial search
		if (append) {
			this.setLoadingMore(true);
		} else {
			this.setLoadingContent(true);
		}
		this.setError(null);

		if (!append) {
			this.resetPaginationState();
			this.clearDirectoryContent();
			this.clearCurrentFolderUserRole();
		}

		// Boundary context travels as a query param (the body is the upstream search DTO),
		// matching the tree and directory-content endpoints so all four agree on scope.
		const params = this.withBoundaryBusinessId({});

		return this.http.post<{ data: SearchResult<FileSystemNode> }>(url, request, { params }).pipe(
			map(envelope => {
				const response = envelope?.data ?? {
					items: [],
					total: 0,
					page: request.page ?? 1,
					limit: request.limit ?? 0,
					totalPages: 0,
					hasNext: false,
					hasPrev: false
				};

				const mappedItems: FileSystemNode[] = (response.items ?? []).map((item: FileSystemNode) => ({
					id: item.id,
					slug: item.slug || item.name,
					name: item.name,
					originalName: item.originalName,
					description: item.description || '',
					extension: item.extension || null,
					mimeType: item.mimeType || null,
					userName: item.userName || '',
					path: item.path || '',
					type: item.type,
					size: item.size || null,
					createdAt: item.createdAt,
					version: item.version || 0,
					tags: item.tags || [],
					isStarred: item.isStarred || false,
					thumbnails: item.thumbnails,
					// Media metadata fields - extract from nested metadata object
					...extractMediaMetadata(item.metadata),
					metadata: item.metadata
				}));

				return {
					...response,
					items: mappedItems
				};
			}),
			tap({
				next: response => {
					this.setDirectoryContent(response.items, append);

					this.setPaginationState({
						currentPage: response.page,
						totalItems: response.total,
						hasMore: response.hasNext
					});

					// Reset the appropriate loading flag based on operation type
					if (append) {
						this.setLoadingMore(false);
					} else {
						this.setLoadingContent(false);
					}
				},
				error: error => {
					console.error('Failed to search files:', error);
					this.setError(error.message || 'Failed to search files');
					// Reset both loading flags on error to be safe
					this.setLoadingContent(false);
					this.setLoadingMore(false);
				}
			}),
			catchError((error: HttpErrorResponse) => {
				// The API now rejects a search scoped outside the caller's boundary (#10774).
				// Unlike folder navigation there is nothing to navigate away from, so surface
				// the message and end the stream, leaving the user on their current view.
				// Pickers pass toastOnForbidden=false to get the 403 in their own error handler
				// and show it inline instead.
				if (error.status === 403 && options?.toastOnForbidden !== false) {
					this.globalService.triggerCustomMessage(
						$localize`:403 boundary error message@@fileLibrary403BoundaryError:You don't have access to this folder. Contact your administrator for access.`,
						'danger'
					);
					this.setLoadingContent(false);
					this.setLoadingMore(false);
					return EMPTY;
				}
				throw error;
			}),
			map(response => response.items)
		);
	}

	/**
	 * Gets autocomplete suggestions for file search
	 */
	suggestFiles(request: SuggestFileSystemRequest): Observable<string[]> {
		const url = `${this.endpoint}/suggest`;
		let params = new HttpParams().set('prefix', request.prefix).set('limit', (request.limit || 10).toString());

		if (request.directoryId) {
			params = params.set('directoryId', request.directoryId);
		}

		// Autocomplete is boundary-scoped server-side and needs the same business
		// context as search, or it can suggest filenames from outside the boundary.
		const businessId = this.resolveBoundaryBusinessId();
		if (businessId) {
			params = params.set('businessId', businessId);
		}

		return this.http.get<{ data: string[] }>(url, { params }).pipe(
			map(envelope => envelope.data),
			catchError(error => {
				console.error('Failed to get suggestions:', error);
				return of([]);
			})
		);
	}

	/**
	 * Creates a new directory in the file library
	 */
	createDirectory(name: string, parentId?: string): Observable<FolderTreeNode> {
		const url = `${this.endpoint}/directory`;

		const body: { name: string; parentId?: string } = { name };
		if (parentId) {
			body.parentId = parentId;
		}

		this.setError(null);

		return this.http.post<FolderTreeNode>(url, body).pipe(
			catchError((error: HttpErrorResponse) => {
				// A read-only viewer creating inside a folder, or a non-platform admin
				// creating at the library root, is refused here. Same treatment as the
				// other 403s in this service: report it and end the stream, so the caller
				// neither refreshes the tree nor reports it a second time.
				if (error.status === 403) {
					this.globalService.triggerCustomMessage(createFolderPermissionDeniedMessage(), 'danger');
					return EMPTY;
				}

				// Deliberately not setError() - that slot feeds the grid's "Unable to load
				// files" banner, which would report a failed *write* as a failure to read
				// the folder. The caller surfaces this one.
				throw error;
			})
		);
	}

	/**
	 * Renames an item (file or folder)
	 */
	renameItem(itemId: string, name: string): Observable<FileSystemNode> {
		const url = `${this.endpoint}/${itemId}/rename`;

		const body = { name };

		this.setError(null);

		return this.http.post<FileSystemNode>(url, body).pipe(
			tap({
				error: error => {
					this.setError(error.message || 'Failed to rename item');
				}
			})
		);
	}

	/**
	 * Updates file or directory details (name and/or description)
	 */
	updateItemDetails(itemId: string, name?: string, description?: string): Observable<FileSystemNode> {
		const url = `${this.endpoint}/${itemId}/details`;

		const body: { name?: string; description?: string | null } = {};
		if (name !== undefined) {
			body.name = name;
		}
		if (description !== undefined) {
			body.description = description === '' ? null : description;
		}

		this.setError(null);

		return this.http.patch<FileSystemNode>(url, body).pipe(
			tap({
				next: updatedNode => {
					// Update the item in directory content store so file browser reflects the change
					this.updateItemInDirectoryContent(updatedNode, itemId);
				},
				error: error => {
					this.setError(error.message || 'Failed to update file details');
				}
			})
		);
	}

	/**
	 * Moves an item (file or folder) to a new destination
	 */
	moveItem(itemId: string, destinationId: string | null): Observable<FolderTreeNode> {
		const url = `${this.endpoint}/${itemId}/move`;

		const body: { destinationId?: string } = {};
		if (destinationId) {
			body.destinationId = destinationId;
		}

		this.setError(null);

		return this.http.post<FolderTreeNode>(url, body).pipe(
			tap({
				error: error => {
					this.setError(error.message || 'Failed to move item');
				}
			})
		);
	}

	/**
	 * Copies an item (file or folder) to a new destination
	 */
	copyItem(itemId: string, destinationId: string | null): Observable<FolderTreeNode> {
		const url = `${this.endpoint}/${itemId}/copy`;

		const body: { destinationId?: string } = {};
		if (destinationId) {
			body.destinationId = destinationId;
		}

		this.setError(null);

		return this.http.post<FolderTreeNode>(url, body).pipe(
			tap({
				error: error => {
					this.setError(error.message || 'Failed to copy item');
				}
			})
		);
	}

	/**
	 * Deletes an item (file or folder) from the file library
	 */
	deleteItem(itemId: string): Observable<void> {
		const url = `${this.endpoint}/${itemId}/delete`;

		this.setError(null);

		return this.http.delete<void>(url).pipe(
			tap({
				error: error => {
					this.setError(error.message || 'Failed to delete item');
				}
			})
		);
	}

	/**
	 * Adds an item to the starred collection
	 */
	addToStarredCollection(itemId: string): Observable<void> {
		const url = `${this.endpoint}/collection/starred/add/${itemId}`;

		this.setError(null);

		return this.http.post<void>(url, null).pipe(
			tap({
				next: () => {
					this.updateItemStarredStatus(itemId, true);
				},
				error: error => {
					this.setError(error.message || 'Failed to add item to starred collection');
				}
			})
		);
	}

	/**
	 * Removes an item from the starred collection
	 */
	removeFromStarredCollection(itemId: string): Observable<void> {
		const url = `${this.endpoint}/collection/starred/remove/${itemId}`;

		this.setError(null);

		return this.http.delete<void>(url).pipe(
			tap({
				next: () => {
					this.updateItemStarredStatus(itemId, false);
				},
				error: error => {
					this.setError(error.message || 'Failed to remove item from starred collection');
				}
			})
		);
	}

	/**
	 * Retrieves the starred collection from the API
	 */
	getStarredCollection(page: number = 1, append: boolean = false, type?: string): Observable<FileSystemNode[]> {
		const state = this.fileLibraryStore.getValue();
		const perPage = state.perPage;
		const url = `${this.endpoint}/collection/starred/content`;

		// Use loadingMore for pagination, loadingContent for initial load
		if (append) {
			this.setLoadingMore(true);
		} else {
			this.setLoadingContent(true);
		}
		this.setSelectedDirectoryId(starredCollectionId);

		if (!append) {
			this.resetPaginationState();
			this.clearDirectoryContent();
			this.clearCurrentFolderUserRole();
		}

		this.setError(null);

		const params: Record<string, string> = {
			page: page.toString(),
			perPage: perPage.toString()
		};

		if (type && type !== 'all') {
			params['type'] = type;
		}

		return this.http
			.get<{
				items: FileSystemNode[];
				page: number;
				perPage: number;
				totalResults?: number;
				numPages?: number;
				hasMore: boolean;
			}>(url, { params })
			.pipe(
				tap({
					next: response => {
						const itemsReturned = response.items?.length || 0;
						const numPages = (response as Record<string, unknown>)['numPages'] as number | undefined;
						const totalResults = (response as Record<string, unknown>)['totalResults'] as number | undefined;

						this.starredItemIds = new Set(response.items.map(item => item.id));
						response.items.forEach(item => this.starredOverrides.set(item.id, true));
						this.setDirectoryContent(
							response.items.map(item => ({ ...item, isStarred: true })),
							append
						);

						const loadedItemsCount = append ? state.totalItems + itemsReturned : itemsReturned;

						const currentPage = response.page || page;
						const calculatedHasMore =
							numPages !== undefined
								? currentPage < numPages
								: totalResults !== undefined
								? loadedItemsCount < totalResults
								: itemsReturned >= perPage;

						this.setPaginationState({
							currentPage,
							totalItems: loadedItemsCount,
							hasMore: calculatedHasMore
						});

						// Reset the appropriate loading flag based on operation type
						if (append) {
							this.setLoadingMore(false);
						} else {
							this.setLoadingContent(false);
						}
					},
					error: error => {
						this.setError(error.message || 'Failed to load starred items');
						// Reset both loading flags on error to be safe
						this.setLoadingContent(false);
						this.setLoadingMore(false);
					}
				}),
				map(response => response.items)
			);
	}

	/**
	 * Loads the next page of starred collection
	 */
	loadMoreStarredCollection(type?: string): Observable<FileSystemNode[]> {
		const state = this.fileLibraryStore.getValue();
		const nextPage = state.currentPage + 1;

		return this.getStarredCollection(nextPage, true, type);
	}

	/**
	 * Downloads files from the file library
	 */
	downloadFiles(ids: string[]): Observable<void> {
		const url = `${this.endpoint}/download`;

		this.setError(null);

		return new Observable<void>(observer => {
			this.http
				.post(
					url,
					{ ids },
					{
						responseType: 'blob',
						observe: 'response',
						headers: {
							'Cache-Control': 'no-cache, no-store, must-revalidate',
							Pragma: 'no-cache',
							Expires: '0'
						}
					}
				)
				.subscribe({
					next: response => {
						if (!response.body) {
							observer.error({ message: $localize`:Message@@fileLibraryServiceEmptyResponseBody:Empty response body` });
							return;
						}

						let downloadFileName = `packaged-files-${Date.now()}.zip`;
						const contentDisposition = response.headers.get('content-disposition');
						if (contentDisposition) {
							const extractedFilename = this.extractFilenameFromContentDisposition(contentDisposition);
							if (extractedFilename) {
								downloadFileName = extractedFilename;
							}
						}

						const blob = response.body;
						const downloadUrl = window.URL.createObjectURL(blob);
						const link = document.createElement('a');
						link.href = downloadUrl;
						link.download = downloadFileName;
						document.body.appendChild(link);
						link.click();
						document.body.removeChild(link);
						window.URL.revokeObjectURL(downloadUrl);
						observer.next();
						observer.complete();
					},
					error: async error => {
						if (error.error instanceof Blob) {
							try {
								const text = await error.error.text();
								const parsedError = JSON.parse(text);
								const httpError = {
									...error,
									error: parsedError,
									message: parsedError.message
								};
								this.setError(parsedError.message || 'Failed to download file');
								observer.error(httpError);
							} catch {
								this.setError(error.message || 'Failed to download file');
								observer.error(error);
							}
						} else {
							this.setError(error.message || 'Failed to download file');
							observer.error(error);
						}
					}
				});
		});
	}

	/**
	 * Starts a file upload with proper subscription management
	 */
	startUpload(file: File, directoryId?: string, options?: { sessionId?: string; relativePath?: string }): UploadResultObservable {
		const tempId = this.addUploadingFile(file, directoryId);

		this.uploadMetadata.set(tempId, {
			uploadCompleted: false,
			sessionId: options?.sessionId ?? null,
			aborted: false
		});

		const upload$ = this.uploadFile(file, directoryId, tempId, options).pipe(
			tap({
				next: () => {
					this.removeUploadingFile(tempId, true);
				},
				error: () => {
					this.removeUploadingFile(tempId, false);
				}
			}),
			finalize(() => {
				this.uploadSubscriptions.delete(tempId);
			})
		);

		const wrappedUpload$ = new Observable<UploadSignal>(subscriber => {
			const container = new Subscription();
			this.registerUploadSubscription(tempId, container);
			const inner = upload$.subscribe(subscriber);
			container.add(inner);
			return container;
		});

		return { tempId, upload$: wrappedUpload$ };
	}

	/**
	 * Initiates a Direct-to-Storage upload
	 */
	initiateUpload(
		file: File,
		directoryId?: string,
		options?: { sessionId?: string; relativePath?: string }
	): Observable<InitiateUploadResponse> {
		const url = `${this.endpoint}/upload/initiate`;

		const MULTIPART_THRESHOLD = 10 * 1024 * 1024; // 10MB
		const strategy = file.size > MULTIPART_THRESHOLD ? 'multipart' : 'post';

		const PART_SIZE = 5 * 1024 * 1024; // 5MB
		const parts = strategy === 'multipart' ? Math.ceil(file.size / PART_SIZE) : undefined;

		const request: InitiateUploadRequest = {
			filename: file.name,
			contentType: file.type || 'application/octet-stream',
			size: file.size,
			strategy,
			parts,
			directoryId,
			sessionId: options?.sessionId,
			relativePath: options?.relativePath,
			initialStatus: 'published'
		};

		return this.http.post<InitiateUploadResponse>(url, request);
	}

	/**
	 * Uploads file directly to S3 using POST strategy
	 */
	uploadPost(file: File, uploadData: PostUploadResponse, tempId?: string): Observable<void> {
		return new Observable<void>(observer => {
			const formData = new FormData();

			Object.entries(uploadData.fields).forEach(([key, value]) => {
				formData.append(key, value as string);
			});

			formData.append('file', file);

			const xhr = new XMLHttpRequest();

			xhr.upload.addEventListener('progress', event => {
				if (event.lengthComputable && tempId) {
					const progress = Math.round((100 * event.loaded) / event.total);
					this.updateUploadProgress(tempId, progress);
				}
			});

			xhr.addEventListener('load', () => {
				if (xhr.status >= 200 && xhr.status < 300) {
					observer.next();
					observer.complete();
				} else {
					const error: Error & { status?: number; statusText?: string } = new Error(`Upload failed with status ${xhr.status}`);
					error.status = xhr.status;
					error.statusText = xhr.statusText;
					observer.error(error);
				}
			});

			xhr.addEventListener('error', () => {
				const error: Error & { status?: number; statusText?: string } = new Error('Upload failed');
				error.status = xhr.status || 0;
				error.statusText = xhr.statusText || 'Unknown Error';
				observer.error(error);
			});

			xhr.addEventListener('abort', () => {
				const cancelError = new Error('Upload cancelled') as Error & { isCancelled: boolean };
				cancelError.isCancelled = true;
				observer.error(cancelError);
			});

			xhr.open('POST', uploadData.url);
			xhr.send(formData);

			return () => {
				try {
					xhr.abort();
				} catch (err) {
					console.error('Error aborting XHR:', err);
				}
			};
		});
	}

	/**
	 * Uploads file directly to S3 using multipart strategy
	 */
	uploadMultipart(file: File, uploadData: MultipartUploadResponse, tempId?: string): Observable<UploadPartResult[]> {
		const PART_SIZE = 5 * 1024 * 1024; // 5MB
		const uploadedParts: UploadPartResult[] = [];
		let uploadedBytes = 0;

		this.debugLog('[uploadMultipart] Starting multipart upload', {
			fileName: file.name,
			fileSize: file.size,
			totalParts: uploadData.parts.length
		});

		const partUploads = uploadData.parts.map(part => {
			const start = (part.number - 1) * PART_SIZE;
			const end = Math.min(start + PART_SIZE, file.size);
			const chunk = file.slice(start, end);
			this.debugLog(`[uploadMultipart] Prepared part ${part.number}`, { start, end, chunkSize: chunk.size });

			return new Observable<UploadPartResult>(observer => {
				const reader: FileReader = new FileReader();
				let xhr: XMLHttpRequest | null = null;

				const cleanup = (): void => {
					try {
						if (xhr) {
							xhr.abort();
						}
					} catch (err) {
						console.error('Error aborting XHR:', err);
					}
					try {
						reader.abort();
					} catch (err) {
						console.error('Error aborting reader:', err);
					}
				};

				reader.onload = async (): Promise<void> => {
					try {
						const arrayBuffer = reader.result as ArrayBuffer;
						// Use Web Crypto API for SHA256 checksum
						const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
						const hashArray = new Uint8Array(hashBuffer);
						const checksum = btoa(String.fromCharCode(...hashArray));

						xhr = new XMLHttpRequest();

						xhr.upload.addEventListener('progress', event => {
							if (event.lengthComputable && tempId) {
								const partProgress = event.loaded;
								const totalProgress = Math.round(((uploadedBytes + partProgress) / file.size) * 100);
								this.updateUploadProgress(tempId, totalProgress);
							}
						});

						xhr.addEventListener('load', () => {
							this.debugLog(`[uploadMultipart] Part ${part.number} load event`, {
								status: xhr?.status,
								statusText: xhr?.statusText
							});
							if (xhr && xhr.status >= 200 && xhr.status < 300) {
								const eTag = xhr.getResponseHeader('ETag');
								if (!eTag) {
									console.error(`[uploadMultipart] Part ${part.number} missing ETag header`);
									observer.error(new Error(`Part ${part.number} missing ETag header`));
									return;
								}

								uploadedBytes += chunk.size;
								const result: UploadPartResult = {
									number: part.number,
									eTag,
									checksumSHA256: checksum
								};
								this.debugLog(`[uploadMultipart] Part ${part.number} completed successfully`, { eTag });
								observer.next(result);
								observer.complete();
							} else {
								console.error(`[uploadMultipart] Part ${part.number} failed`, {
									status: xhr?.status,
									statusText: xhr?.statusText,
									responseText: xhr?.responseText
								});
								const error: Error & { status?: number; statusText?: string } = new Error(
									`Part ${part.number} upload failed with status ${xhr?.status}`
								);
								error.status = xhr?.status;
								error.statusText = xhr?.statusText;
								observer.error(error);
							}
						});

						xhr.addEventListener('error', event => {
							console.error(`[uploadMultipart] Part ${part.number} XHR error event`, {
								status: xhr?.status,
								statusText: xhr?.statusText,
								event
							});
							const error: Error & { status?: number; statusText?: string; error?: ProgressEvent } = new Error(
								`Part ${part.number} upload failed`
							);
							error.status = xhr?.status || 0;
							error.statusText = xhr?.statusText || 'Unknown Error';
							error.error = event;
							observer.error(error);
						});

						xhr.addEventListener('abort', () => {
							this.debugLog(`[uploadMultipart] Part ${part.number} aborted`);
							const cancelError = new Error(`Part ${part.number} upload cancelled`) as Error & {
								isCancelled: boolean;
							};
							cancelError.isCancelled = true;
							observer.error(cancelError);
						});

						this.debugLog(`[uploadMultipart] Part ${part.number} sending to S3`, { url: part.url.substring(0, 100) + '...' });
						xhr.open('PUT', part.url);
						xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
						xhr.send(chunk);
					} catch (error) {
						observer.error(new Error(`Part ${part.number} checksum calculation failed: ${error}`));
					}
				};

				reader.onerror = (): void => {
					observer.error(new Error(`Part ${part.number} failed to read chunk`));
				};

				reader.readAsArrayBuffer(chunk);

				return () => {
					cleanup();
				};
			});
		});

		return new Observable<UploadPartResult[]>(observer => {
			let currentSub: Subscription | null = null;

			const uploadNext = (index: number): void => {
				if (index >= partUploads.length) {
					this.debugLog('[uploadMultipart] All parts completed', { totalParts: uploadedParts.length });
					observer.next(uploadedParts);
					observer.complete();
					return;
				}

				this.debugLog(`[uploadMultipart] Starting part ${index + 1} of ${partUploads.length}`);
				currentSub = partUploads[index].subscribe({
					next: result => {
						this.debugLog(`[uploadMultipart] Part ${index + 1} uploaded, proceeding to next`);
						uploadedParts.push(result);
						uploadNext(index + 1);
					},
					error: error => {
						console.error(`[uploadMultipart] Part ${index + 1} error, aborting multipart upload`, { error });
						observer.error(error);
					}
				});
			};

			uploadNext(0);

			return () => {
				this.debugLog('[uploadMultipart] Observable teardown called', { completedParts: uploadedParts.length });
				if (currentSub) {
					currentSub.unsubscribe();
				}
			};
		});
	}

	/**
	 * Completes a Direct-to-Storage upload
	 */
	completeUpload(uploadId: string, key: string, parts?: UploadPartResult[]): Observable<FileSystemNode> {
		const url = `${this.endpoint}/upload/complete`;

		const request: CompleteUploadRequest = {
			uploadId,
			key,
			parts
		};

		return this.http.post<FileSystemNode>(url, request).pipe(
			map(item => ({
				...item,
				// Extract width/height/duration/frameRate from nested metadata (if available)
				...extractMediaMetadata(item.metadata as Record<string, unknown> | undefined)
			}))
		);
	}

	/**
	 * Gets a temporary presigned URL for a file
	 */
	getTemporaryFileURL(id: string): Observable<string> {
		return this.http.get<{ url: string }>(`${this.endpoint}/${id}/presigned`).pipe(map(response => response.url));
	}

	/**
	 * Gets a temporary presigned URL for a file thumbnail
	 */
	getThumbnailPresignedUrl(id: string, format: string, size: string): Observable<string> {
		return this.http
			.get<{ url: string }>(`${this.endpoint}/${id}/thumbnails/${format}/${size}/presigned`)
			.pipe(map(response => response.url));
	}

	/**
	 * Gets a file or directory by ID
	 */
	getFileById(id: string): Observable<FileSystemNode> {
		return this.http.get<FileSystemNode>(`${this.endpoint}/${id}`).pipe(
			map(item => ({
				...item,
				// Extract width/height/duration/frameRate from nested metadata
				...extractMediaMetadata(item.metadata as Record<string, unknown> | undefined)
			}))
		);
	}

	/**
	 * Aborts a Direct-to-Storage upload
	 */
	abortUpload(uploadId: string): Observable<void> {
		const url = `${this.endpoint}/upload/abort/${uploadId}`;
		return this.http.delete<void>(url);
	}

	/**
	 * Performs a complete Direct-to-Storage upload workflow
	 */
	uploadFile(
		file: File,
		directoryId?: string,
		tempId?: string,
		options?: { sessionId?: string; relativePath?: string }
	): Observable<UploadSignal> {
		this.debugLog('[uploadFile] Starting upload', { fileName: file.name, size: file.size, type: file.type, directoryId, tempId });

		return new Observable<UploadSignal>(observer => {
			let uploadId: string;
			let key: string;
			let uploadCompleted = false;
			const deferSessionCompletion = !!options?.sessionId;

			const sub = this.initiateUpload(file, directoryId, options)
				.pipe(
					switchMap((initResponse: InitiateUploadResponse) => {
						this.debugLog('[uploadFile] Initiate response received', {
							uploadId: initResponse.uploadId,
							strategy: initResponse.strategy,
							key: initResponse.key
						});
						uploadId = initResponse.uploadId;
						key = initResponse.key;

						if (tempId) {
							const metadata = this.uploadMetadata.get(tempId) ?? {
								uploadCompleted: false,
								sessionId: options?.sessionId ?? null
							};
							this.uploadMetadata.set(tempId, { ...metadata, uploadId });
						}

						if (initResponse.strategy === 'post') {
							this.debugLog('[uploadFile] Using POST strategy');
							return this.uploadPost(file, initResponse, tempId).pipe(map(() => undefined));
						} else {
							this.debugLog('[uploadFile] Using MULTIPART strategy');
							return this.uploadMultipart(file, initResponse, tempId);
						}
					}),
					switchMap((parts: UploadPartResult[] | undefined) => {
						this.debugLog('[uploadFile] Upload to storage complete', { partsCount: parts?.length });
						if (deferSessionCompletion) {
							this.debugLog('[uploadFile] Deferring session completion');
							uploadCompleted = true;
							if (tempId) {
								this.markUploadCompleted(tempId);
							}
							return of<UploadSignal>({ kind: 'session' });
						}

						this.debugLog('[uploadFile] Calling completeUpload', { uploadId, key });
						return this.completeUpload(uploadId, key, parts).pipe(
							tap(() => {
								this.debugLog('[uploadFile] completeUpload succeeded');
								uploadCompleted = true;
								if (tempId) {
									this.markUploadCompleted(tempId);
								}
							}),
							map(uploadedNode => ({ kind: 'file', node: uploadedNode } as const))
						);
					}),
					switchMap((signal: UploadSignal) => {
						if (signal.kind !== 'file') {
							return of(signal);
						}

						const refreshDirectoryId = directoryId ?? '__root';

						// A multi-file upload can finish long after the user moved to another
						// folder, and getDirectoryContent writes into the shared
						// directoryContent slot - refreshing here would drop this folder's
						// files into whatever list is on screen. The upload toaster still
						// reports the completion either way, with a link back to the folder.
						if (!this.isViewingDirectory(refreshDirectoryId)) {
							return of(signal);
						}

						return this.getDirectoryContent(refreshDirectoryId, false, 1, false, undefined, undefined, {
							discardIfNavigatedAway: true
						}).pipe(map(() => signal));
					})
				)
				.subscribe({
					next: signal => {
						this.debugLog('[uploadFile] Upload complete successfully', { signal });
						observer.next(signal);
						observer.complete();
					},
					error: error => {
						console.error('[uploadFile] Upload error', {
							error,
							errorMessage: error?.message,
							errorStatus: error?.status,
							uploadId,
							uploadCompleted
						});
						const isCancelled = (error as { isCancelled?: boolean })?.isCancelled === true;
						if (uploadId && !uploadCompleted && !isCancelled) {
							this.debugLog('[uploadFile] Aborting upload due to error', { uploadId });
							this.abortUpload(uploadId).subscribe({
								error: abortError => {
									console.warn('Failed to abort upload:', abortError);
								}
							});
						}

						if (tempId) {
							this.removeUploadingFile(tempId, false);
						}

						// Deliberately not setError() - that slot feeds the grid's "Unable to
						// load files" banner, so a failed upload into an empty folder replaced
						// the empty state with a connectivity message. removeUploadingFile()
						// above records the failure for the upload toaster, and the caller's
						// onError raises the snackbar.

						observer.error(error);
					}
				});

			return () => {
				this.debugLog('[uploadFile] Observable teardown called', { uploadId, uploadCompleted });
				sub.unsubscribe();
			};
		});
	}

	/**
	 * Creates a folder upload session
	 */
	createUploadSession(payload: CreateUploadSessionRequest): Observable<CreateUploadSessionResponse> {
		const url = `${this.endpoint}/upload/session`;
		this.setError(null);
		return this.http.post<CreateUploadSessionResponse>(url, payload);
	}

	/**
	 * Completes a folder upload session
	 */
	completeUploadSession(sessionId: string): Observable<void> {
		const url = `${this.endpoint}/upload/complete`;
		this.setError(null);
		return this.http.post<void>(url, { sessionId });
	}

	/**
	 * Extracts filename from Content-Disposition header
	 */
	private extractFilenameFromContentDisposition(contentDisposition: string): string | null {
		if (!contentDisposition) {
			return null;
		}

		const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''(.+?)(?:;|$)/i);
		if (filenameStarMatch?.[1]) {
			try {
				return decodeURIComponent(filenameStarMatch[1]);
			} catch (error) {
				console.warn('Failed to decode filename* from Content-Disposition', error);
			}
		}

		const quotedMatch = contentDisposition.match(/(?:^|;\s*)filename="([^"]+)"/i);
		if (quotedMatch?.[1]) {
			return quotedMatch[1];
		}

		const singleQuotedMatch = contentDisposition.match(/(?:^|;\s*)filename='([^']+)'/i);
		if (singleQuotedMatch?.[1]) {
			return singleQuotedMatch[1];
		}

		const unquotedMatch = contentDisposition.match(/(?:^|;\s*)filename=([^;\s"']+)/i);
		if (unquotedMatch?.[1] && !unquotedMatch[1].startsWith('*')) {
			return unquotedMatch[1];
		}

		return null;
	}

	/**
	 * Registers an upload subscription for cancellation support
	 */
	private registerUploadSubscription(tempId: string, subscription: Subscription): void {
		this.uploadSubscriptions.set(tempId, subscription);
	}

	/**
	 * Marks an upload as completed in metadata
	 */
	private markUploadCompleted(tempId: string): void {
		const metadata = this.uploadMetadata.get(tempId);
		if (metadata) {
			this.uploadMetadata.set(tempId, { ...metadata, uploadCompleted: true });
		}
	}

	/**
	 * Updates cached state with the latest starred flag for an item
	 */
	private updateItemStarredStatus(itemId: string, isStarred: boolean): void {
		if (isStarred) {
			this.starredItemIds.add(itemId);
		} else {
			this.starredItemIds.delete(itemId);
		}
		this.starredOverrides.set(itemId, isStarred);

		const state = this.fileLibraryStore.getValue();

		const isStarredView = state.selectedDirectoryId === starredCollectionId;
		const shouldRemoveFromContent = isStarredView && !isStarred;

		const directoryContent = shouldRemoveFromContent
			? state.directoryContent.filter(node => node.id !== itemId)
			: this.enrichWithStarredMetadata(state.directoryContent.map(node => (node.id === itemId ? { ...node, isStarred } : node)));

		const uploadingFiles = state.uploadingFiles.map(uploading =>
			uploading.file.id === itemId
				? {
						...uploading,
						file: {
							...uploading.file,
							isStarred
						}
				  }
				: uploading
		);

		this.fileLibraryStore.update({
			directoryContent,
			uploadingFiles
		});
	}

	private enrichWithStarredMetadata(nodes: FileSystemNode[]): FileSystemNode[] {
		return nodes.map(node => {
			const override = this.starredOverrides.get(node.id);
			if (override !== undefined) {
				return {
					...node,
					isStarred: override
				};
			}

			const isMarked = this.starredItemIds.has(node.id);
			return {
				...node,
				isStarred: node.isStarred === true || isMarked
			};
		});
	}

	// ============================================================================
	// Collections Management
	// ============================================================================

	/**
	 * Concurrency limit for batch operations to avoid overwhelming the API
	 */
	private readonly BATCH_CONCURRENCY = 5;

	/**
	 * Sets loading state for collections
	 */
	setLoadingCollections(loading: boolean): void {
		this.fileLibraryStore.update({ loadingCollections: loading });
	}

	/**
	 * Sets the selected collection ID
	 */
	setSelectedCollectionId(collectionId: string | null): void {
		this.fileLibraryStore.update({ selectedCollectionId: collectionId });
	}

	/**
	 * Fetches all collections from the API
	 */
	getCollections(): Observable<FileCollection[]> {
		this.setLoadingCollections(true);
		this.fileLibraryStore.update({ collectionsError: null });

		return this.http.get<FileCollection[]>(`${this.endpoint}/collections`).pipe(
			tap(collections => {
				this.fileLibraryStore.update({ collections });
			}),
			catchError(error => {
				console.error('Failed to fetch collections', error);
				const message = 'Failed to load collections';
				this.fileLibraryStore.update({ collectionsError: message });
				return of([]);
			}),
			finalize(() => {
				this.setLoadingCollections(false);
			})
		);
	}

	/**
	 * Creates a new collection
	 * @param name - Collection name (1-100 chars)
	 */
	createCollection(name: string): Observable<FileCollection> {
		const payload = { name, type: FileCollectionType.CUSTOM };

		return this.http.post<FileCollection>(`${this.endpoint}/collection/create`, payload).pipe(
			tap(collection => {
				const state = this.fileLibraryStore.getValue();
				this.fileLibraryStore.update({
					collections: [...state.collections, collection]
				});
			}),
			catchError(error => {
				// Handle duplicate name (409 Conflict)
				if (error.status === 409) {
					throw new Error('A collection with this name already exists');
				}
				console.error('Failed to create collection', error);
				throw new Error('Failed to create collection');
			})
		);
	}

	/**
	 * Renames an existing collection
	 * @param id - Collection ID
	 * @param name - New name (1-100 chars)
	 */
	renameCollection(id: string, name: string): Observable<FileCollection> {
		const payload = { name };

		return this.http.put<FileCollection>(`${this.endpoint}/collection/${id}`, payload).pipe(
			tap(updated => {
				const state = this.fileLibraryStore.getValue();
				const collections = state.collections.map(c => (c.id === id ? updated : c));
				this.fileLibraryStore.update({ collections });
			}),
			catchError(error => {
				// Handle duplicate name (409 Conflict)
				if (error.status === 409) {
					throw new Error('A collection with this name already exists');
				}
				console.error('Failed to rename collection', error);
				throw new Error('Failed to rename collection');
			})
		);
	}

	/**
	 * Deletes a collection (files remain in library)
	 * @param id - Collection ID
	 */
	deleteCollection(id: string): Observable<void> {
		return this.http.delete<void>(`${this.endpoint}/collection/${id}`).pipe(
			tap(() => {
				const state = this.fileLibraryStore.getValue();
				const collections = state.collections.filter(c => c.id !== id);
				this.fileLibraryStore.update({ collections });

				// Clear selection if deleted collection was selected
				if (state.selectedCollectionId === id) {
					this.fileLibraryStore.update({ selectedCollectionId: null });
				}
			}),
			catchError(error => {
				console.error('Failed to delete collection', error);
				throw new Error('Failed to delete collection');
			})
		);
	}

	/**
	 * Loads paginated content for a collection
	 * Reuses directoryContent field in state for display
	 * @param id - Collection ID
	 * @param page - Page number (1-indexed)
	 * @param perPage - Items per page
	 */
	loadCollectionContent(id: string, page: number = 1, perPage: number = 50): Observable<PaginatedDirectoryContent> {
		// Use loadingMore for pagination (page > 1), loadingContent for initial load (page === 1)
		if (page === 1) {
			this.fileLibraryStore.update({ loadingContent: true });
			this.resetPaginationState();
			this.clearDirectoryContent();
			this.clearCurrentFolderUserRole();
		} else {
			this.setLoadingMore(true);
		}

		const params = new HttpParams().set('page', page.toString()).set('perPage', perPage.toString());

		return this.http.get<PaginatedDirectoryContent>(`${this.endpoint}/collection/${id}/content`, { params }).pipe(
			tap(response => {
				const enrichedItems = this.enrichWithStarredMetadata(response.items);

				// Replace content on first page, append on subsequent pages
				if (page === 1) {
					// Set selectedCollectionId so removeItemFromCollection can update UI
					this.fileLibraryStore.update({ directoryContent: enrichedItems, selectedCollectionId: id });
				} else {
					const state = this.fileLibraryStore.getValue();
					this.fileLibraryStore.update({
						directoryContent: [...state.directoryContent, ...enrichedItems]
					});
				}

				this.setPaginationState({
					currentPage: response.page,
					totalItems: response.totalItems,
					hasMore: response.hasMore
				});
			}),
			catchError(error => {
				console.error('Failed to load collection content', error);
				this.setError('Failed to load collection content');
				return of({
					items: [],
					page: 1,
					perPage,
					totalItems: 0,
					hasMore: false
				});
			}),
			finalize(() => {
				// Reset the appropriate loading flag based on operation type
				if (page === 1) {
					this.fileLibraryStore.update({ loadingContent: false });
				} else {
					this.setLoadingMore(false);
				}
			})
		);
	}

	/**
	 * Adds a single item to a collection
	 * @param collectionId - Collection ID
	 * @param nodeId - File/folder ID to add
	 */
	addItemToCollection(collectionId: string, nodeId: string): Observable<void> {
		return this.http.post<void>(`${this.endpoint}/collection/${collectionId}/add/${nodeId}`, {}).pipe(
			tap(() => {
				// Optimistically increment collection item count
				this.updateCollectionItemCount(collectionId, 1);
			}),
			catchError(error => {
				// Idempotent - adding item already in collection succeeds silently
				if (error.status === 409) {
					return of(undefined);
				}
				console.error('Failed to add item to collection', error);
				throw new Error('Failed to add item to collection');
			})
		);
	}

	/**
	 * Adds multiple items to a collection with concurrency limiting
	 * @param collectionId - Collection ID
	 * @param nodeIds - Array of file/folder IDs to add
	 */
	addItemsToCollection(collectionId: string, nodeIds: string[]): Observable<BatchOperationResult> {
		if (nodeIds.length === 0) {
			return of({
				succeeded: [],
				failed: [],
				totalAttempted: 0,
				totalSucceeded: 0
			});
		}

		const results: { succeeded: string[]; failed: { nodeId: string; error: string }[] } = {
			succeeded: [],
			failed: []
		};

		return from(nodeIds).pipe(
			// Limit concurrency to avoid overwhelming the API
			mergeMap(
				nodeId =>
					this.http.post<void>(`${this.endpoint}/collection/${collectionId}/add/${nodeId}`, {}).pipe(
						map(() => {
							results.succeeded.push(nodeId);
							return nodeId;
						}),
						catchError(error => {
							// Idempotent - treat 409 (already in collection) as success
							if (error.status === 409) {
								results.succeeded.push(nodeId);
								return of(nodeId);
							}
							results.failed.push({
								nodeId,
								error: error.message || 'Failed to add item'
							});
							return of(null);
						})
					),
				this.BATCH_CONCURRENCY
			),
			toArray(),
			map(() => {
				// Update collection item count optimistically
				this.updateCollectionItemCount(collectionId, results.succeeded.length);

				return {
					succeeded: results.succeeded,
					failed: results.failed,
					totalAttempted: nodeIds.length,
					totalSucceeded: results.succeeded.length
				};
			})
		);
	}

	/**
	 * Removes an item from a collection (does not delete the file)
	 * @param collectionId - Collection ID
	 * @param nodeId - File/folder ID to remove
	 */
	removeItemFromCollection(collectionId: string, nodeId: string): Observable<void> {
		return this.http.delete<void>(`${this.endpoint}/collection/${collectionId}/remove/${nodeId}`).pipe(
			tap(() => {
				// Optimistically decrement collection item count
				this.updateCollectionItemCount(collectionId, -1);

				// Remove from directoryContent if viewing this collection
				const state = this.fileLibraryStore.getValue();
				if (state.selectedCollectionId === collectionId) {
					const directoryContent = state.directoryContent.filter(item => item.id !== nodeId);
					this.fileLibraryStore.update({ directoryContent });
				}
			}),
			catchError(error => {
				console.error('Failed to remove item from collection', error);
				throw new Error('Failed to remove item from collection');
			})
		);
	}

	/**
	 * Updates the item count for a collection optimistically
	 */
	private updateCollectionItemCount(collectionId: string, delta: number): void {
		const state = this.fileLibraryStore.getValue();
		const collections = state.collections.map(c => {
			if (c.id === collectionId && c.itemCount !== undefined) {
				return { ...c, itemCount: Math.max(0, c.itemCount + delta) };
			}
			return c;
		});
		this.fileLibraryStore.update({ collections });
	}

	// ============================================================================
	// Tags & Metadata Management
	// ============================================================================

	/**
	 * Gets the attributes (tags and custom metadata) for a file node
	 * @param fileNodeId - The file node ID
	 * @returns Observable of file attributes or null if not found
	 */
	getFileAttributes(fileNodeId: string): Observable<FileNodeAttributes | null> {
		const url = `${this.endpoint}/${fileNodeId}/attributes`;

		return this.http.get<FileNodeAttributes>(url).pipe(
			catchError(error => {
				if (error.status === 404) {
					return of(null);
				}
				console.error('Failed to get file attributes:', error);
				this.setError(error.message || 'Failed to get file attributes');
				throw error;
			})
		);
	}

	/**
	 * Updates tags on a file node using add/remove/set operations
	 * @param fileNodeId - The file node ID
	 * @param request - Tag update operations
	 * @returns Observable of updated file attributes
	 */
	updateFileTags(fileNodeId: string, request: UpdateTagsRequest): Observable<FileNodeAttributes> {
		const url = `${this.endpoint}/${fileNodeId}/tags`;

		this.setError(null);

		return this.http.patch<FileNodeAttributes>(url, request).pipe(
			tap({
				next: attributes => {
					// Update tags in directory content if the file is displayed
					this.updateFileTagsInStore(fileNodeId, attributes.tags);
				},
				error: error => {
					console.error('Failed to update file tags:', error);
					this.setError(error.message || 'Failed to update file tags');
				}
			})
		);
	}

	/**
	 * Adds tags to a file node
	 * Convenience method that wraps updateFileTags
	 * @param fileNodeId - The file node ID
	 * @param tags - Tags to add
	 */
	addFileTags(fileNodeId: string, tags: string[]): Observable<FileNodeAttributes> {
		return this.updateFileTags(fileNodeId, { add: tags });
	}

	/**
	 * Removes tags from a file node
	 * Convenience method that wraps updateFileTags
	 * @param fileNodeId - The file node ID
	 * @param tags - Tags to remove
	 */
	removeFileTags(fileNodeId: string, tags: string[]): Observable<FileNodeAttributes> {
		return this.updateFileTags(fileNodeId, { remove: tags });
	}

	/**
	 * Sets (replaces) all tags on a file node
	 * Convenience method that wraps updateFileTags
	 * @param fileNodeId - The file node ID
	 * @param tags - Tags to set
	 */
	setFileTags(fileNodeId: string, tags: string[]): Observable<FileNodeAttributes> {
		return this.updateFileTags(fileNodeId, { set: tags });
	}

	/**
	 * Updates custom metadata on a file node (shallow merge)
	 * @param fileNodeId - The file node ID
	 * @param request - Metadata to merge
	 * @returns Observable of updated file attributes
	 */
	updateFileMetadata(fileNodeId: string, request: UpdateMetadataRequest): Observable<FileNodeAttributes> {
		const url = `${this.endpoint}/${fileNodeId}/metadata`;

		this.setError(null);

		return this.http.patch<FileNodeAttributes>(url, request).pipe(
			tap({
				next: attributes => {
					// Update metadata in directory content store so file browser reflects the change
					this.updateFileMetadataInStore(fileNodeId, attributes.customMetadata);
				},
				error: error => {
					console.error('Failed to update file metadata:', error);
					this.setError(error.message || 'Failed to update file metadata');
				}
			})
		);
	}

	/**
	 * Updates the customMetadata property on a file in the directory content store
	 */
	private updateFileMetadataInStore(fileNodeId: string, customMetadata: Record<string, unknown> | undefined): void {
		const state = this.fileLibraryStore.getValue();
		const updatedContent = state.directoryContent.map(item =>
			item.id === fileNodeId ? { ...item, customMetadata: customMetadata || {} } : item
		);

		if (state.directoryContent.some(item => item.id === fileNodeId)) {
			this.fileLibraryStore.update({ directoryContent: updatedContent });
		}
	}

	/**
	 * Bulk updates tags across multiple file nodes
	 * @param request - Bulk update request with file node IDs and tag operations
	 * @returns Observable of bulk operation result with succeeded/failed arrays
	 */
	bulkUpdateFileTags(request: BulkUpdateTagsRequest): Observable<BulkOperationResult> {
		const url = `${this.endpoint}/bulk/tags`;

		this.setError(null);

		return this.http.post<BulkOperationResult>(url, request).pipe(
			tap({
				next: result => {
					// Optimistically update tags in store for succeeded files
					for (const fileId of result.succeeded) {
						this.updateFileTagsOptimistically(fileId, request.add, request.remove);
					}
				},
				error: error => {
					console.error('Failed to bulk update file tags:', error);
					this.setError(error.message || 'Failed to bulk update file tags');
				}
			})
		);
	}

	/**
	 * Optimistically updates tags for a file in the store based on add/remove operations.
	 * Since the bulk API doesn't return new tag values, we compute them from operations.
	 */
	private updateFileTagsOptimistically(fileId: string, add?: string[], remove?: string[]): void {
		const directoryContent = this.fileLibraryStore.getValue().directoryContent;
		const existingItem = directoryContent?.find(item => item.id === fileId);
		if (!existingItem) return;

		const currentTags = existingItem.tags ?? [];
		let newTags = [...currentTags];

		// Remove tags
		if (remove?.length) {
			const removeSet = new Set(remove.map(t => t.toLowerCase()));
			newTags = newTags.filter(t => !removeSet.has(t.toLowerCase()));
		}

		// Add tags (avoid duplicates)
		if (add?.length) {
			const existingSet = new Set(newTags.map(t => t.toLowerCase()));
			for (const tag of add) {
				if (!existingSet.has(tag.toLowerCase())) {
					newTags.push(tag);
				}
			}
		}

		this.updateFileTagsInStore(fileId, newTags);
	}

	/**
	 * Gets tag suggestions for the organization (autocomplete)
	 * @param prefix - Optional prefix to filter suggestions
	 * @param limit - Maximum number of suggestions to return
	 * @returns Observable of tag strings
	 */
	getTagSuggestions(prefix?: string, limit: number = 20): Observable<string[]> {
		const url = `${this.endpoint}/tags/suggestions`;

		let params = new HttpParams().set('limit', limit.toString());
		if (prefix) {
			params = params.set('prefix', prefix);
		}

		return this.http.get<{ tags: string[] }>(url, { params }).pipe(
			map(response => response?.tags ?? []),
			catchError(error => {
				console.error('Failed to get tag suggestions:', error);
				return of([]);
			})
		);
	}

	/**
	 * Searches for file node IDs by tags
	 * @param tags - Tags to search for
	 * @param matchAll - If true, matches files with ALL tags; if false, matches files with ANY tag
	 * @returns Observable of file node IDs
	 */
	searchByTags(tags: string[], matchAll: boolean = true): Observable<string[]> {
		const url = `${this.endpoint}/search/by-tags`;

		const params = new HttpParams().set('tags', tags.join(',')).set('matchAll', matchAll.toString());

		return this.http.get<string[]>(url, { params }).pipe(
			catchError(error => {
				console.error('Failed to search by tags:', error);
				this.setError(error.message || 'Failed to search by tags');
				return of([]);
			})
		);
	}

	/**
	 * Updates the tags property on a file in the directory content store
	 */
	private updateFileTagsInStore(fileNodeId: string, tags: string[]): void {
		const state = this.fileLibraryStore.getValue();
		const updatedContent = state.directoryContent.map(item => (item.id === fileNodeId ? { ...item, tags } : item));

		if (state.directoryContent.some(item => item.id === fileNodeId)) {
			this.fileLibraryStore.update({ directoryContent: updatedContent });
		}
	}

	// ============================================================================
	// Filter Management
	// ============================================================================

	/**
	 * Sets a single filter value
	 *
	 * @example
	 * // Set starred filter
	 * service.setFilter('starred', true);
	 *
	 * // Add extensions
	 * service.setFilter('extensions', ['jpg', 'png']);
	 */
	setFilter<K extends keyof ActiveFilters>(key: K, value: ActiveFilters[K]): void {
		this.fileLibraryStore.update(state => ({
			activeFilters: { ...state.activeFilters, [key]: value }
		}));
	}

	/**
	 * Sets multiple filter values at once
	 * Useful when applying a filter preset or URL params
	 *
	 * @example
	 * service.setFilters({
	 *   extensions: ['jpg', 'png'],
	 *   starred: true
	 * });
	 */
	setFilters(filters: Partial<ActiveFilters>): void {
		this.fileLibraryStore.update(state => ({
			activeFilters: { ...state.activeFilters, ...filters }
		}));
	}

	/**
	 * Clears all active filters to initial state
	 */
	clearFilters(): void {
		this.fileLibraryStore.update({ activeFilters: createEmptyFilters() });
	}

	/**
	 * Clears a single filter category
	 *
	 * @example
	 * service.clearFilter('tags'); // Clears tags array
	 * service.clearFilter('dateRange'); // Clears date range
	 */
	clearFilter<K extends keyof ActiveFilters>(key: K): void {
		let emptyValue: ActiveFilters[K];
		if (key === 'starred') {
			emptyValue = null as ActiveFilters[K];
		} else if (key === 'dateRange' || key === 'yearRange') {
			emptyValue = { from: null, to: null } as ActiveFilters[K];
		} else {
			emptyValue = [] as ActiveFilters[K];
		}
		this.setFilter(key, emptyValue);
	}

	/**
	 * Loads available filter options from the API
	 * Called when entering a folder to know what's available to filter by
	 *
	 * @param directoryId - Optional directory to scope filter options
	 * @returns Observable of filter options
	 */
	loadFilterOptions(directoryId?: string): Observable<FilterOptions> {
		this.fileLibraryStore.update({ loadingFilterOptions: true });

		let params = new HttpParams();
		if (directoryId && directoryId !== '__root') {
			params = params.set('directoryId', directoryId);
		}

		return this.http.get<FilterOptions>(`${this.endpoint}/filter-options`, { params }).pipe(
			tap(options => {
				this.fileLibraryStore.update({
					filterOptions: options,
					loadingFilterOptions: false
				});
			}),
			catchError(error => {
				console.error('Failed to load filter options:', error);
				this.fileLibraryStore.update({ loadingFilterOptions: false });
				// Return empty options on error - UI will just have no suggestions
				return of({ tags: [], extensions: [] });
			})
		);
	}

	/**
	 * Load admin-configured search filters for the file library.
	 */
	loadSavedSearchFilters(): Observable<FileLibrarySearchFilter[]> {
		const siteId = environment.organizationId || '';
		if (!siteId) {
			return of([]);
		}

		this.fileLibraryStore.update({ loadingSavedSearchFilters: true });
		const url = `${environment.apiUrl}/site/${siteId}/file-library/search-filters`;

		return this.http.get<FileLibrarySearchFilter[]>(url).pipe(
			tap(filters => {
				const sorted = [...filters].sort((a, b) => a.filterName.localeCompare(b.filterName));
				this.fileLibraryStore.update({
					savedSearchFilters: sorted,
					loadingSavedSearchFilters: false
				});
			}),
			catchError(error => {
				console.error('Failed to load saved search filters:', error);
				this.fileLibraryStore.update({
					savedSearchFilters: [],
					loadingSavedSearchFilters: false
				});
				return of([]);
			})
		);
	}

	/**
	 * Builds search request params from active filters
	 * This is the bridge between UI filter state and API contract
	 *
	 * @param filters - Current active filters
	 * @returns Partial SearchFileSystemRequest for API call
	 */
	buildFilterParams(filters: ActiveFilters): Partial<SearchFileSystemRequest> {
		const params: Partial<SearchFileSystemRequest> = {};

		if (filters.tags.length > 0) {
			params.tags = filters.tags;
		}
		if (filters.extensions.length > 0) {
			params.extensions = filters.extensions;
		}
		if (filters.types.length > 0) {
			params.types = filters.types;
		}
		if (filters.metadataSearch.length > 0) {
			params.metadataSearch = filters.metadataSearch;
		}
		if (filters.starred !== null) {
			params.starred = filters.starred;
		}

		let createdAfter: string | undefined;
		let createdBefore: string | undefined;

		if (filters.dateRange.from) {
			createdAfter = filters.dateRange.from.includes('T') ? filters.dateRange.from : `${filters.dateRange.from}T00:00:00.000Z`;
		}
		if (filters.dateRange.to) {
			createdBefore = filters.dateRange.to.includes('T') ? filters.dateRange.to : `${filters.dateRange.to}T23:59:59.999Z`;
		}

		if (filters.yearRange.from !== null || filters.yearRange.to !== null) {
			const yearFrom = filters.yearRange.from ?? filters.yearRange.to!;
			const yearTo = filters.yearRange.to ?? filters.yearRange.from!;

			const yearAfter = `${yearFrom}-01-01T00:00:00.000Z`;
			const yearBefore = `${yearTo}-12-31T23:59:59.999Z`;

			createdAfter = this.maxDate(createdAfter, yearAfter);
			createdBefore = this.minDate(createdBefore, yearBefore);
		}

		if (createdAfter) {
			params.createdAfter = createdAfter;
		}
		if (createdBefore) {
			params.createdBefore = createdBefore;
		}

		return params;
	}

	private maxDate(first: string | undefined, second: string): string {
		if (!first) {
			return second;
		}

		return Date.parse(first) > Date.parse(second) ? first : second;
	}

	private minDate(first: string | undefined, second: string): string {
		if (!first) {
			return second;
		}

		return Date.parse(first) < Date.parse(second) ? first : second;
	}
	// ============================================================================
	// Duplicate File Resolution
	// ============================================================================

	/**
	 * Check for duplicates and resolve conflicts before upload.
	 * Returns files ready to upload after user resolves any conflicts.
	 *
	 * @param files - Files to upload
	 * @param directoryId - Target directory ID (null for root)
	 * @returns Promise of files to upload after conflict resolution
	 */
	async resolveConflictsAndUpload(files: File[], directoryId: string | null): Promise<File[]> {
		const filesToUpload: File[] = [];
		let applyToAllResolution: DuplicateResolution | null = null;

		for (let i = 0; i < files.length; i++) {
			const file = files[i];
			const isDuplicate = await this.checkIsDuplicate(file.name, directoryId);

			if (!isDuplicate) {
				filesToUpload.push(file);
				continue;
			}

			// If "Apply to all" was selected, use that resolution
			if (applyToAllResolution) {
				const resolved = this.applyResolution(file, applyToAllResolution);
				if (resolved) filesToUpload.push(resolved);
				continue;
			}

			// Show dialog for this conflict
			const result = await this.showDuplicateDialog(file, files.length - i - 1);

			if (!result) {
				// User cancelled - skip remaining files
				break;
			}

			if (result.applyToAll) {
				applyToAllResolution = result.resolution;
			}

			const resolved = this.applyResolution(file, result.resolution);
			if (resolved) filesToUpload.push(resolved);
		}

		return filesToUpload;
	}

	/**
	 * Checks if a file with the given name exists in the upload queue or directory.
	 *
	 * @param filename - File name to check
	 * @param directoryId - Directory to check (null for root)
	 * @returns True if duplicate exists
	 */
	private async checkIsDuplicate(filename: string, directoryId: string | null): Promise<boolean> {
		// Check upload queue first (in-memory check)
		const queueState = this.uploadQueueService.getStateSnapshot();
		const existsInQueue = queueState.items.some(item => item.file.name === filename && item.directoryId === directoryId);
		if (existsInQueue) return true;

		// Check current directory content in store
		const state = this.fileLibraryStore.getValue();
		const existsInContent = state.directoryContent.some(item => item.name === filename && item.type === 'file');

		return existsInContent;
	}

	/**
	 * Shows the duplicate file resolution dialog.
	 *
	 * @param file - The file that has a duplicate
	 * @param remainingConflicts - Number of remaining files that might have conflicts
	 * @returns Promise of user's resolution choice, or null if cancelled
	 */
	private showDuplicateDialog(file: File, remainingConflicts: number): Promise<DuplicateFileDialogResult | null> {
		const dialogRef = this.dialog.open(DuplicateFileDialogComponent, {
			data: {
				filename: file.name,
				newFile: file,
				isImage: file.type.startsWith('image/'),
				remainingConflicts
			} as DuplicateFileDialogData,
			width: '450px',
			disableClose: true
		});

		return firstValueFrom(dialogRef.afterClosed());
	}

	/**
	 * Applies the user's resolution choice to a file.
	 *
	 * @param file - The file to process
	 * @param resolution - The resolution choice
	 * @returns The file to upload (possibly renamed), or null to skip
	 */
	private applyResolution(file: File, resolution: DuplicateResolution): File | null {
		switch (resolution) {
			case 'replace':
				// Upload with same name (will overwrite on server)
				return file;

			case 'keep-both':
				// Auto-rename: image.jpg → image (1).jpg
				const lastDotIndex = file.name.lastIndexOf('.');
				const hasExtension = lastDotIndex > 0;
				const ext = hasExtension ? file.name.substring(lastDotIndex) : '';
				const base = hasExtension ? file.name.substring(0, lastDotIndex) : file.name;
				const timestamp = Date.now();
				const newName = `${base} (${timestamp})${ext}`;
				return new File([file], newName, { type: file.type, lastModified: file.lastModified });

			case 'skip':
				// Don't upload this file
				return null;
		}
	}

	// ============================================================================
	// Business Context Management
	// ============================================================================

	/**
	 * Subscribes to active business changes and triggers state reset.
	 * This ensures file library data is fresh when the user switches businesses.
	 */
	private subscribeToBusinessChanges(): void {
		this.businessQuery
			.selectActiveId()
			.pipe(
				takeUntil(this.destroy$),
				distinctUntilChanged(),
				// Skip the initial null/undefined value - only react to actual business switches
				filter((businessId): businessId is string => businessId !== null && businessId !== undefined)
			)
			.subscribe(businessId => {
				this.onBusinessChange(businessId);
			});
	}

	/**
	 * Handles business context change by resetting file library state.
	 *
	 * This method:
	 * 1. Cancels all in-flight requests
	 * 2. Resets the Akita store to initial state
	 * 3. Clears internal caches (starred items, upload metadata)
	 * 4. Fetches fresh directory tree for the new business context
	 *
	 * @param newBusinessId - The ID of the newly selected business
	 */
	private onBusinessChange(newBusinessId: string): void {
		// Signal cancellation to any in-flight requests
		this.businessSwitch$.next();

		// Cancel all active upload subscriptions
		this.uploadSubscriptions.forEach((subscription, tempId) => {
			subscription.unsubscribe();
		});
		this.uploadSubscriptions.clear();

		// Clear internal caches
		this.uploadMetadata.clear();
		this.starredItemIds.clear();
		this.starredOverrides.clear();

		// Reset the Akita store to initial state
		this.fileLibraryStore.reset();

		// Explicitly clear selection state (reset() should handle this, but be explicit)
		this.fileLibraryStore.update({
			selectedDirectoryId: null,
			selectedCollectionId: null
		});

		// Fetch fresh directory tree for the new business context
		this.getDirectoryTree()
			.pipe(takeUntil(this.businessSwitch$))
			.subscribe({
				error: error => {
					console.error('Failed to fetch directory tree after business switch:', error);
				}
			});
	}
}
